/*!
 * Copyright © 2018-2019. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { NotificationsClientService } from '@fleetmatics/ui.notifications/client';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { isElementNull, isStringNullOrEmpty } from '../../../../utils';
import { ENotificationSettingsTypes, INotificationResponse } from '../../../core/components/notifications';
import { AddNotifications, DeletePanicNotification } from '../../../core/components/notifications/store';
import { IFieldServiceMessageResponse, ISpeedViolationMessage, IVehiclePlotWebsubsResponse } from '../../../core/models';
import {
  StartVehiclesPolling,
  StopVehiclesPolling,
  UpdateScheduledJob,
  UpdateVehicles,
  UpdateVehicleSpeed
} from '../../../core/store/actions';
import { IAppState } from '../../../core/store/state';
import { UpdateVehicleDiagnostic } from '../../../ecm/store/actions';
import { IWebsocketConfig } from '../../models';
import { IWebsocketConnectorService } from './websocket-connector.service.interface';

@Injectable()
export class WebsocketConnectorService implements IWebsocketConnectorService {
  private readonly connection: HubConnection;
  private vehiclesToSubscribe: string[] = [];
  private reconnectAttempts = 0;

  constructor(
    private readonly store: Store<IAppState>,
    private readonly notificationsClientService: NotificationsClientService
  ) {
    this.connection = this.notificationsClientService.connection;
  }

  public async start(config: IWebsocketConfig): Promise<void> {
    this.notificationsClientService.addDefaultHandlers();

    this.subscribeToEvents();
    this.subscribeFsdMessage();

    this.setupConnectionFallback();

    try {
      await this.notificationsClientService.start();
    } catch (error) {
      this.startVehiclesPolling();
    }
  }

  public subscribeVehicles(vehicles: string[]): void {
    this.vehiclesToSubscribe = vehicles;

    if (this.connection.state === HubConnectionState.Connected) {
      this.connection.invoke('SubscribeVehiclesAsync', vehicles.join(','));
    }
  }

  private setupConnectionFallback(): void {
    // Handle disconnections with errors
    this.connection.onclose(error => {
      if (error && this.reconnectAttempts === 0) {
        this.startVehiclesPolling();
      }
      if (error) {
        this.reconnectAttempts++;
      }
    });

    // Handle reconnection attempts
    this.connection.onreconnecting(() => {
      if (this.reconnectAttempts === 0) {
        this.startVehiclesPolling();
      }
      this.reconnectAttempts++;
    });

    // Handle successful reconnection
    this.connection.onreconnected(() => {
      this.reconnectAttempts = 0;
      this.stopVehiclesPolling();
    });
  }

  private startVehiclesPolling(): void {
    this.store.dispatch(new StartVehiclesPolling());
  }

  private stopVehiclesPolling(): void {
    this.store.dispatch(new StopVehiclesPolling());
  }

  private subscribeToEvents(): void {
    this.notificationsClientService.addHandler('Connected', () => {
      this.connection.invoke('SubscribeVehiclesAsync', this.vehiclesToSubscribe.join(','));
    });

    this.notificationsClientService.addHandler('rVehiclePlotMessage', (vehicle: IVehiclePlotWebsubsResponse) => {
      this.store.dispatch(new UpdateVehicles(vehicle));
      this.store.dispatch(new UpdateVehicleDiagnostic(vehicle));
    });

    this.notificationsClientService.addHandler('rVehicleSpeedViolationMessage', (notification: ISpeedViolationMessage) => {
      this.store.dispatch(new UpdateVehicleSpeed(notification));
    });

    this.notificationsClientService.addHandler('rWebPanicOffNotificationMessage', (notification: INotificationResponse) => {
      this.store.dispatch(new DeletePanicNotification(notification.vid));
    });

    this.notificationsClientService.addHandler('rWebNotificationMessage', (notification: INotificationResponse) => {
      if (notification.type === ENotificationSettingsTypes.fleetServiceReminder) {
        return;
      }
      this.store.dispatch(new AddNotifications(notification));
    });

    this.notificationsClientService.addHandler('rReportNotificationMessage', (notification: INotificationResponse) => {
      this.store.dispatch(new AddNotifications(notification));
    });

    this.notificationsClientService.addHandler('rBalloonCacheConnectionStatus', (connectionState: boolean) => {
      if (connectionState) {
        this.stopVehiclesPolling();
      } else {
        this.startVehiclesPolling();
      }
    });
  }

  private subscribeFsdMessage(): void {
    this.notificationsClientService.addHandler('FsdGenericWebMessage', (message: IFieldServiceMessageResponse) => {
      if (
        !isElementNull(message) &&
        !isElementNull(message.Data) &&
        (message.Data.subscriptionName === 'VisitNotificationUpdatedEvent' ||
          message.Data.subscriptionName === 'VisitTransitionUpdatedEvent' ||
          message.Data.subscriptionName === 'fsd-visit-eta') &&
        !isStringNullOrEmpty(message.Data.payload)
      ) {
        this.store.dispatch(new UpdateScheduledJob(JSON.parse(message.Data.payload)));
      }
    });
  }
}
